import {
  BASE64_REGEXP,
  DecorativeReason,
  MICRO_IMAGE_SIZE,
  MIN_IMAGE_SIZE,
  SVG_REGEXP,
} from './constants';
import { hasMinAllowedSize, getImageSource } from './image-utils';
import { isExistingAltInvalid } from './utils/is-existing-alt-invalid';
import { isExcludedDomainImage } from './utils/is-excluded-domain-image';
import { hasWrongSrc } from './utils/has-wrong-src';
import { hasWrongAspectRatio } from './utils/has-wrong-aspect-ratio';

export const shouldBeDecorative = (
  imageElement: HTMLImageElement,
): DecorativeReason | null => {
  const imageSrc = getImageSource(imageElement);
  const isInvalidAlt = isExistingAltInvalid(imageElement.alt);

  // # image src has wrong URL (high priority check)
  const isImageSrcWrong = hasWrongSrc(imageSrc);
  if (isImageSrcWrong) {
    return DecorativeReason.WRONG_SRC;
  }

  // # image src includes excluded domain  (high priority check)
  const isImageDomainExcluded = isExcludedDomainImage(imageSrc);
  if (isImageDomainExcluded) {
    return DecorativeReason.EXCLUDED_SRC;
  }

  // # image is very small (high priority check)
  const isMicroImage = !hasMinAllowedSize(imageElement, MICRO_IMAGE_SIZE);
  if (isMicroImage) {
    return DecorativeReason.MICRO_SIZE;
  }

  // # image has wrong aspect ratio (high priority check)
  const isWrongAspectRatio = hasWrongAspectRatio(imageElement);
  if (isWrongAspectRatio) {
    return DecorativeReason.ASPECT_RATIO;
  }

  // # image is base 64
  // TODO: add support of base64 after BE implementation of sending src hash to alt enpoint
  const isBase64 = BASE64_REGEXP.test(imageSrc);

  if (isBase64) {
    return DecorativeReason.BASE64;
  }

  // # image is hidden for screen readers
  const hiddenForReaders =
    imageElement.getAttribute('aria-hidden') === 'true' ||
    imageElement.getAttribute('role') === 'presentation' ||
    imageElement.getAttribute('role') === 'none';

  if (hiddenForReaders) {
    return DecorativeReason.HIDDEN_FROM_SCREEN_READER;
  }

  // # image is svg and doesn't have own alt attribute
  const isSvgFile = SVG_REGEXP.test(imageSrc);

  if (isSvgFile && isInvalidAlt) {
    return DecorativeReason.SVG;
  }

  // # image is smaller than allowed size and doesn't have own alt attribute or has invalid alt
  const hasCorrectSize = hasMinAllowedSize(imageElement, MIN_IMAGE_SIZE);
  if (!hasCorrectSize && isInvalidAlt) {
    return DecorativeReason.SMALL_SIZE;
  }

  return null;
};
